/**
 * Public contact block for BaseTemplate* — Tally form on consulta.delfuocampo.com.
 *
 * Submissions: Tally → POST /api/tally-webhook (PostHog `contact_form_submitted` server-side).
 * Client: postMessage from embed → PostHog funnel (view / submit).
 *
 * Friendly UX: soft intro, lazy embed near viewport, loading shimmer, full-page fallback.
 */

import { ExternalLink, Mail, MessageCircle } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  trackBaseTemplateClick,
  type BaseTemplateAnalyticsName,
} from "@/lib/baseTemplateAnalytics";

/** Custom domain for the Delfu contact form (Tally Pro). */
export const TALLY_CONTACT_PUBLIC_URL = "https://consulta.delfuocampo.com/";

/**
 * Embed params: hide Tally chrome, forward form events, grow height with content.
 * Server webhook is separate: `POST /api/tally-webhook` (configured in Tally dashboard).
 */
export const TALLY_CONTACT_EMBED_SRC =
  "https://consulta.delfuocampo.com/?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1&formEventsForwarding=1&embed=1";

const TALLY_EMBED_SCRIPT = "https://tally.so/widgets/embed.js";

type Props = {
  countryCode?: string;
  /** Analytics template name (BaseTemplate | BaseTemplateBlack | …). */
  templateName?: BaseTemplateAnalyticsName;
  /** Light = white section (BaseTemplate rich). Dark = black section (BaseTemplateBlack). */
  variant?: "light" | "dark";
  className?: string;
};

function hydrateTallyEmbeds() {
  const w = window as unknown as { Tally?: { loadEmbeds: () => void } };
  try {
    w.Tally?.loadEmbeds?.();
  } catch {
    /* */
  }
  document
    .querySelectorAll<HTMLIFrameElement>("iframe[data-tally-src]:not([src])")
    .forEach((iframe) => {
      const src = iframe.dataset.tallySrc;
      if (src) iframe.src = src;
    });
}

export function BaseTemplateTallyContact({
  countryCode,
  templateName = "BaseTemplate",
  variant = "light",
  className,
}: Props) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement | null>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [iframeReady, setIframeReady] = useState(false);
  const titleId = useId();
  const isDark = variant === "dark";

  // Lazy: only load script + iframe when section is near the fold.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setNearViewport(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNearViewport(true);
          io.disconnect();
        }
      },
      { rootMargin: "240px 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Load Tally embed script once when near viewport.
  useEffect(() => {
    if (!nearViewport) return;
    if (!document.querySelector(`script[src="${TALLY_EMBED_SCRIPT}"]`)) {
      const s = document.createElement("script");
      s.src = TALLY_EMBED_SCRIPT;
      s.async = true;
      s.onload = () => hydrateTallyEmbeds();
      s.onerror = () => hydrateTallyEmbeds();
      document.body.appendChild(s);
    } else {
      // SPA re-entry: re-hydrate this page’s iframe.
      hydrateTallyEmbeds();
    }
  }, [nearViewport]);

  // PostMessage funnel → PostHog (webhook covers server-side submit too).
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (typeof e.data !== "string" || !e.data.includes("Tally.")) return;
      let evt: string | undefined;
      try {
        evt = (JSON.parse(e.data) as { event?: string }).event;
      } catch {
        return;
      }
      if (!evt) return;
      const ph = (
        window as unknown as {
          posthog?: { capture?: (n: string, p?: Record<string, unknown>) => void };
        }
      ).posthog;
      const base = {
        surface: "contact",
        template: templateName,
        country_code: countryCode,
        form_host: "consulta.delfuocampo.com",
        webhook: "/api/tally-webhook",
      };
      if (evt.includes("PageView") || evt.includes("Loaded")) {
        ph?.capture?.("contact_form_viewed", base);
      } else if (evt.includes("Submit")) {
        ph?.capture?.("contact_form_submitted", {
          ...base,
          source: "tally_embed_postmessage",
        });
        trackBaseTemplateClick({
          templateName,
          elementId: "contact_form_submit_success",
          label: "Contact form submitted (Tally)",
          surface: "contact",
          countryCode,
        });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [countryCode, templateName]);

  return (
    <section
      ref={sectionRef}
      id="contacto"
      data-section-name="Contact"
      aria-labelledby={titleId}
      className={cn(
        "relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16 md:px-8 md:py-20",
        isDark ? "bg-black text-white" : "bg-zinc-50 text-zinc-900",
        className,
      )}
    >
      {/* Soft ambient glow — friendly, not corporate form wall */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b",
          isDark
            ? "from-violet-500/10 to-transparent"
            : "from-violet-200/40 to-transparent",
        )}
      />

      <div className="relative mx-auto max-w-2xl">
        <div className="mb-8 text-center sm:mb-10">
          <p
            className={cn(
              "mb-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]",
              isDark
                ? "border-white/15 bg-white/[0.06] text-white/70"
                : "border-zinc-200 bg-white text-zinc-500",
            )}
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden />
            {t("baseTemplate.contact.eyebrow", { defaultValue: "Contacto" })}
          </p>
          <h2
            id={titleId}
            className={cn(
              "font-display text-2xl font-bold tracking-wide sm:text-3xl md:text-4xl",
              isDark ? "text-white" : "text-zinc-900",
            )}
          >
            {t("baseTemplate.contact.title", { defaultValue: "Escribime" })}
          </h2>
          <p
            className={cn(
              "mx-auto mt-3 max-w-md text-sm leading-relaxed sm:text-base",
              isDark ? "text-white/65" : "text-zinc-600",
            )}
          >
            {t("baseTemplate.contact.subtitle", {
              defaultValue:
                "Consultas, colaboraciones o prensa. Dejame tu mensaje y te respondo a la brevedad.",
            })}
          </p>
          <p
            className={cn(
              "mt-2 text-[12px]",
              isDark ? "text-white/40" : "text-zinc-400",
            )}
          >
            {t("baseTemplate.contact.eta", {
              defaultValue: "Respuesta habitual en 24–48 h · sin spam",
            })}
          </p>
        </div>

        <div
          className={cn(
            "overflow-hidden rounded-2xl border shadow-xl",
            isDark
              ? "border-white/10 bg-zinc-950 shadow-black/40"
              : "border-zinc-200/80 bg-black shadow-zinc-900/15",
          )}
        >
          {/* Toolbar */}
          <div
            className={cn(
              "flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5",
              isDark ? "border-white/10 bg-white/[0.03]" : "border-white/10 bg-zinc-900",
            )}
          >
            <span className="inline-flex items-center gap-1.5 text-[12px] text-white/55">
              <Mail className="h-3.5 w-3.5" aria-hidden />
              {t("baseTemplate.contact.formLabel", {
                defaultValue: "Formulario seguro",
              })}
            </span>
            <a
              href={TALLY_CONTACT_PUBLIC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-1 text-[11px] font-medium text-white/75 transition hover:border-white/25 hover:bg-white/10 hover:text-white"
              onClick={() =>
                trackBaseTemplateClick({
                  templateName,
                  elementId: "contact_form_open_fullpage",
                  label: "Open Tally full page",
                  surface: "contact",
                  countryCode,
                })
              }
            >
              {t("baseTemplate.contact.openFull", {
                defaultValue: "Abrir completo",
              })}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          </div>

          <div className="relative w-full min-h-[420px] sm:min-h-[520px] bg-black">
            {!iframeReady && (
              <div
                className="absolute inset-0 z-[1] flex flex-col items-center justify-center gap-3 bg-zinc-950 px-6"
                aria-hidden={!iframeReady}
              >
                <div className="h-9 w-9 animate-spin rounded-full border-2 border-white/15 border-t-violet-400" />
                <p className="text-center text-[12px] text-white/45">
                  {t("baseTemplate.contact.loading", {
                    defaultValue: "Cargando formulario…",
                  })}
                </p>
              </div>
            )}

            {nearViewport ? (
              <iframe
                data-tally-src={TALLY_CONTACT_EMBED_SRC}
                loading="lazy"
                width="100%"
                height="520"
                frameBorder={0}
                marginHeight={0}
                marginWidth={0}
                title={t("baseTemplate.contact.iframeTitle", {
                  defaultValue: "Formulario de contacto",
                })}
                className={cn(
                  "w-full min-h-[420px] border-0 sm:min-h-[520px] transition-opacity duration-300",
                  iframeReady ? "opacity-100" : "opacity-0",
                )}
                onLoad={() => {
                  setIframeReady(true);
                  // Re-run Tally height resize after paint.
                  window.setTimeout(() => hydrateTallyEmbeds(), 50);
                }}
              />
            ) : (
              <div className="flex min-h-[420px] items-center justify-center sm:min-h-[520px]">
                <p className="text-[12px] text-white/35">
                  {t("baseTemplate.contact.scrollHint", {
                    defaultValue: "Desplazá un poco para cargar el formulario…",
                  })}
                </p>
              </div>
            )}
          </div>
        </div>

        <p
          className={cn(
            "mt-4 text-center text-[11px] leading-relaxed",
            isDark ? "text-white/35" : "text-zinc-400",
          )}
        >
          {t("baseTemplate.contact.privacy", {
            defaultValue:
              "Tus datos se usan solo para responderte. No compartimos tu email con terceros.",
          })}
        </p>
      </div>
    </section>
  );
}

export default BaseTemplateTallyContact;
